import { useEffect, useRef, useState, type MouseEvent } from "react";
import { detachTagFromChat, pinChat, renameChat, unpinChat } from "../../api/whatsapp.js";
import { openLightbox } from "../../lib/lightbox.js";
import type { ChatTag, Lead, WhatsappChat } from "../../types.ts";
import { chatSubtitleFor, contrastFor } from "../Chat.helpers.ts";
import { ChatAvatar, SvgIcon } from "../chat-list/ChatAvatar.tsx";
import { ChatLeadStager } from "../ChatLeadStager.tsx";
import { icons } from "./icons.ts";
import type { TagPickerState } from "./types.ts";

type RoomHeaderProps = {
  chat: WhatsappChat;
  display: string;
  lead: Lead | null;
  tags: ChatTag[];
  onBack: () => void;
  onShowLead: () => void;
  onScheduleCadence: () => void;
  onOpenTagPicker: (position: TagPickerState) => void;
  onRefresh: () => void;
  avatarUrl: string | null;
  avatarLoading: boolean;
  onLoadAvatar: () => void;
  onError: (message: string) => void;
  onInfo: (message: string) => void;
};

export function RoomHeader({
  chat,
  display,
  lead,
  tags,
  onBack,
  onShowLead,
  onScheduleCadence,
  onOpenTagPicker,
  onRefresh,
  avatarUrl,
  avatarLoading,
  onLoadAvatar,
  onError,
  onInfo,
}: RoomHeaderProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const attachedTags = (chat.tagIds || [])
    .map((id) => tags.find((tag) => tag.id === id))
    .filter((tag): tag is ChatTag => !!tag);
  const isPinned = !!chat.pinnedAt;

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDocumentMouseDown = (event: globalThis.MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && moreMenuRef.current?.contains(target)) return;
      setMoreOpen(false);
    };
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDocumentMouseDown);
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [moreOpen]);

  async function togglePin() {
    try {
      if (isPinned) await unpinChat(chat.chatJid);
      else await pinChat(chat.chatJid);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Falha");
    }
  }

  async function saveContact() {
    const nextName = window.prompt(
      "Salvar este contato como:\n(o nome aparece aqui e no card do lead, se vinculado)",
      display,
    );
    if (!nextName || !nextName.trim() || nextName.trim() === display) return;
    try {
      await renameChat(chat.chatJid, nextName.trim());
      onInfo("Contato salvo");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Falha ao salvar contato");
    }
  }

  function openTagPickerFrom(event: MouseEvent<HTMLButtonElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    setMoreOpen(false);
    onOpenTagPicker({ x: Math.min(rect.left, window.innerWidth - 280), y: rect.bottom + 6 });
  }

  return (
    <header className="wa-room-header">
      <div className="wa-room-info">
        <button className="wa-icon-btn wa-room-back" type="button" title="Voltar para conversas" aria-label="Voltar para conversas" onClick={onBack}>
          <SvgIcon svg={icons.back} />
        </button>
        <ChatAvatar
          chat={chat}
          display={display}
          large
          avatarUrl={avatarUrl}
          onClick={avatarUrl ? () => openLightbox({
            src: avatarUrl,
            kind: "image",
            caption: display,
          }) : undefined}
        />
        <div className="wa-room-meta">
          <div className="wa-room-title-line">
            <span className="wa-room-title">{display}</span>
            {isPinned ? (
              <span className="wa-room-status-chip" title="Conversa fixada">
                <SvgIcon svg={icons.pin} />
                <span>Fixado</span>
              </span>
            ) : null}
          </div>
          <div className="wa-room-sub">{chatSubtitleFor(chat)}</div>
        </div>
      </div>
      {attachedTags.length ? (
        <div className="wa-room-tags" aria-label="Tags do contato">
          {attachedTags.map((tag) => (
            <button
              key={tag.id}
              className="wa-room-tag"
              type="button"
              title={`Remover tag ${tag.name}`}
              aria-label={`Remover tag ${tag.name}`}
              style={{ background: tag.color, color: contrastFor(tag.color) }}
              onClick={() => detachTagFromChat(chat.chatJid, tag.id).catch((err: unknown) => {
                onError(err instanceof Error ? err.message : "Falha");
              })}
            >
              <span className="wa-room-tag-name">{tag.name}</span>
              <span className="wa-room-tag-x" aria-hidden="true">x</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="wa-room-actions" aria-label="Acoes da conversa">
        <div className="wa-room-primary-actions">
          {lead ? (
            <>
              <button className="wa-action-btn" type="button" title="Ver lead" onClick={onShowLead}>
                <SvgIcon svg={icons.user} />
                <span className="wa-action-btn-label">Lead</span>
              </button>
              <button className="wa-action-btn" type="button" title="Agendar cadencia" onClick={onScheduleCadence}>
                <SvgIcon svg={icons.calendar} />
                <span className="wa-action-btn-label">Agendar cadencia</span>
              </button>
            </>
          ) : null}
          <button
            className="wa-action-btn"
            type="button"
            title="Buscar foto do perfil"
            onClick={onLoadAvatar}
            disabled={avatarLoading}
          >
            <SvgIcon svg={icons.photo} />
            <span className="wa-action-btn-label">{avatarLoading ? "Buscando" : "Foto"}</span>
          </button>
          <button className="wa-icon-btn" type="button" title="Pesquisar nesta conversa" aria-label="Pesquisar nesta conversa">
            <SvgIcon svg={icons.search} />
          </button>
          <button className="wa-icon-btn" type="button" title="Atualizar" aria-label="Atualizar" onClick={onRefresh}>
            <SvgIcon svg={icons.refresh} />
          </button>
        </div>
        <div className="wa-room-more" ref={moreMenuRef}>
          <button
            className="wa-room-more-trigger"
            type="button"
            title="Mais ações"
            aria-label="Mais ações"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((open) => !open)}
          >
            <SvgIcon svg={icons.menu} />
          </button>
          {moreOpen ? (
            <div className="wa-room-more-menu" role="menu" aria-label="Mais ações da conversa">
              <div className="wa-room-more-section" role="none">
                <ChatLeadStager chat={chat} display={display} lead={lead} />
              </div>
              <button
                className="wa-room-more-item"
                type="button"
                role="menuitem"
                title="Salvar nome do contato (aparece aqui e no card do lead)"
                onClick={() => {
                  setMoreOpen(false);
                  void saveContact();
                }}
              >
                <SvgIcon svg={icons.edit} />
                <span>Salvar contato</span>
              </button>
              <button
                className="wa-room-more-item"
                type="button"
                role="menuitem"
                title="Atribuir/remover tags"
                onClick={openTagPickerFrom}
              >
                <SvgIcon svg={icons.tag} />
                <span>Tags</span>
              </button>
              <button
                className={`wa-room-more-item ${isPinned ? "is-active" : ""}`}
                type="button"
                role="menuitem"
                title={isPinned ? "Desfixar conversa" : "Fixar conversa"}
                onClick={() => {
                  setMoreOpen(false);
                  void togglePin();
                }}
              >
                <SvgIcon svg={icons.pin} />
                <span>{isPinned ? "Desfixar" : "Fixar"}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
